import { zodResolver } from '@hookform/resolvers/zod'
import { FilterAlt, FilterAltOff } from '@mui/icons-material'
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Icon,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { DataGrid, GridColDef, GridColumnMenuProps, GridRowSelectionModel, GridSearchIcon } from '@mui/x-data-grid'
import { useEffect, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import toast from 'react-hot-toast'
import Chip from 'src/@core/components/mui/chip'
import { AsyncSelect, Button, ControlledCheckBox, ControlledTextField } from 'src/components/forms'
import { ControlledAutocomplete } from 'src/components/forms/ControlledAutocomplete'
import { HttpCacheUser } from 'src/http/cache/user/httpCacheUser'
import { HttpUser } from 'src/http/user/httpUser'
import { HttpSystems } from 'src/http/workflow/httpSystems'
import { EntityWithIdFields } from 'src/types'
import {
  UserDTOFields,
  UserGroupFilterDTOFields,
  UserGroupViewDTO,
  UserGroupViewDTOFields,
  UserInsertDTO,
  UserViewDTO
} from 'src/types/user/userData'
import { z } from 'zod'
import { CountryAvatar } from "../../../../../../components/icons/CountryAvatar"
import EditIcon from '@mui/icons-material/Edit'
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'
import CountryFilter from '../../components/filters/CountryFilter'
import WorkflowFilter from '../../components/filters/WorkflowFilter'

export type EditUserProps = {
  user: UserViewDTO
  onClose: () => void,
  mode: 'edit' | 'view'
}

const schemaFormPostUsuario = z.object({
  [UserDTOFields.FirstName]: z.string(),
  [UserDTOFields.LastName]: z.string(),
  [UserDTOFields.Email]: z.string().email(),
  [UserDTOFields.PhoneNumber]: z.string().nullable().optional(),
  [UserDTOFields.UserPositionCode]: z.number()
})

type SchemaFormPostUsuario = z.infer<typeof schemaFormPostUsuario>

const schemaFiltroUsuarioGrupo = z.object({
  [UserGroupFilterDTOFields.CodsMensajeTipo]: z.array(z.number()).optional(),
  [UserGroupFilterDTOFields.CodsSistemaPrincipal]: z.array(z.number()).optional(),
  [UserGroupFilterDTOFields.GroupName]: z.string().optional(),
  [UserGroupFilterDTOFields.Assigned]: z.boolean().optional(),
  [UserGroupFilterDTOFields.SoloActivos]: z.boolean().optional()
})

type SchemaFiltroUsuarioGrupo = z.infer<typeof schemaFiltroUsuarioGrupo>


function EditUser({ user, onClose, mode }: EditUserProps) {
  const { getPaisLogoPorCodigo, paisLogos, loading } = usePaisLogo()
  const [formDisabled, setFormDisabled] = useState(mode === 'view')
  const { control, handleSubmit, } = useForm<SchemaFormPostUsuario>({
    resolver: zodResolver(schemaFormPostUsuario),
    defaultValues: {
      ...user
    },
  })

  const columnas: GridColDef<UserGroupViewDTO>[] = [
    {
      headerName: 'Grupo',
      field: UserGroupViewDTOFields.GroupName,
      flex: 3
    },
    {
      headerName: 'País',
      field: UserGroupViewDTOFields.CountryName,
      flex: 1,
      renderCell: ({ row: group }) => {
        return (
          <Chip
            size='small'
            label={group[UserGroupViewDTOFields.CountryName]}
            avatar={
              <CountryAvatar
                countryCode={group[UserGroupViewDTOFields.CountryCode]}
              />
            }
          />
        )
      }
    },
    {
      headerName: 'Circuito',
      field: UserGroupViewDTOFields.NombreSistema,
      flex: 4
    }
  ]

  const submitUser = (data: UserInsertDTO) => {
    return user?.id ? HttpUser.updateUser(user.id, data) : HttpUser.insertUser(data)
  }

  const onSubmitWrapper = () => {
    let mensajeError = 'No se pudo guardar el usuario';

    const promise = new Promise<void>((resolve, reject) => {
      handleSubmit(
        formData => {
          submitUser(formData)
            .then(response => {
              if (response.tieneError) {
                mensajeError = response.descripcionError;
                reject(response.descripcionError);
              } else {
                const userId = user?.id || response.data;

                HttpUser.updateUserGroups(userId, (rowSelectionModel as number[]) || [])
                  .then(() => {
                    setTimeout(onClose, 500);
                    resolve();
                  })
                  .catch(reject);
              }
            })
            .catch(reject);
        },
        validationErrors => {
          mensajeError = 'Revise el formulario';
          reject(JSON.stringify(validationErrors));
        }
      )();
    });

    toast.promise(promise, {
      success: 'Guardado',
      error: () => mensajeError,
      loading: 'Guardando'
    });

    return promise;
  }

  const [groups, setGroups] = useState<UserGroupViewDTO[]>()

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>()

  const onCancelHandler = () => {
    onClose()
  }

  const {
    control: controlFiltro,
    handleSubmit: handleSubmitFiltro,
    resetField: resetFieldFiltro
  } = useForm<SchemaFiltroUsuarioGrupo>({
    defaultValues: {
      [UserGroupFilterDTOFields.SoloActivos]: true,
    },
    resolver: zodResolver(schemaFiltroUsuarioGrupo)
  })

  const watchSoloActivos = useWatch({ control: controlFiltro, name: UserGroupFilterDTOFields.SoloActivos })

  useEffect(() => {
    handleSubmitFiltro(setFilterAndSearch)()
  }, [watchSoloActivos])

  const setFilterAndSearch = (data?: SchemaFiltroUsuarioGrupo) => {
    return HttpUser.getGroupsByUser(user[EntityWithIdFields.Id], data).then(groups => {
      setGroups(groups)
      console.log('cantidad grupos', groups.length);
      const selectedGroups = groups
        .filter(group => group[UserGroupViewDTOFields.GroupAssigned])
        .map(group => group[UserGroupViewDTOFields.GroupId])
      setRowSelectionModel(prev => [...new Set([...selectedGroups, ...(prev || [])])])
    })
  }

  const onFilterChange = () => {
    handleSubmitFiltro(setFilterAndSearch)()
  }

  return (
    <>
      <DialogTitle>
        <Stack direction='row' spacing={2} alignItems='center'>
          {'Modificación de usuario'}

          <Tooltip title='Editar'>
            <IconButton
              onClick={() => setFormDisabled(!formDisabled)}
            >
              {
                formDisabled ? <EditIcon color='info' /> : <GridSearchIcon color='info' />
              }

            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Stack spacing={2}>

              <Typography>General</Typography>
              <Divider />
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <ControlledTextField
                      control={control}
                      name={UserDTOFields.FirstName}
                      label='Nombre'
                      fullWidth
                      disabled={formDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ControlledTextField
                      control={control}
                      name={UserDTOFields.LastName}
                      label='Apellido'
                      fullWidth
                      disabled={formDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ControlledTextField
                      control={control}
                      name={UserDTOFields.Email}
                      label='Email'
                      fullWidth
                      disabled={formDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <AsyncSelect
                      control={control}
                      name={UserDTOFields.UserPositionCode}
                      label='Posición'
                      loadOptions={HttpCacheUser.getPositionList}
                      fullWidth
                      disabled={formDisabled}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack spacing={2}>
              <Stack direction={'row'} alignItems={'center'} spacing={4}>
                <Typography>Grupos</Typography>
                <ControlledCheckBox
                  label={'Solo Activos'}
                  name={UserGroupFilterDTOFields.SoloActivos}
                  control={controlFiltro} />
              </Stack>

              <Divider />
              <Box sx={{ height: '100%' }}>
                <DataGrid
                  disableColumnFilter
                  keepNonExistentRowsSelected
                  columns={columnas}
                  checkboxSelection
                  rowSelectionModel={rowSelectionModel}
                  onRowSelectionModelChange={setRowSelectionModel}
                  rows={groups ? groups : []}
                  getRowId={row => row[UserGroupViewDTOFields.GroupId]}
                  density='compact'
                  pagination
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 25, page: 0 },
                    },
                  }}
                  pageSizeOptions={[25, 50, 75, 100]}
                  slots={{
                    columnMenu: (props: GridColumnMenuProps) => {
                      const {
                        colDef: { field }
                      } = props

                      if (field === UserGroupViewDTOFields.GroupName) {
                        return (
                          <Stack
                            direction='row'
                            spacing={2}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              p: 2
                            }}
                          >
                            <Controller
                              control={controlFiltro}
                              name={UserGroupFilterDTOFields.GroupName}
                              render={({ field, fieldState }) => {
                                return (
                                  <TextField
                                    {...field}
                                    fullWidth
                                    size='small'
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                  />
                                )
                              }}
                            />
                            <Button type='submit' onClick={onFilterChange}>
                              Buscar
                            </Button>
                            <Tooltip title='Limpiar filtro'>
                              <IconButton
                                onClick={() => {
                                  resetFieldFiltro(UserGroupFilterDTOFields.GroupName)
                                  onFilterChange() // search on filter reset
                                }}
                              >
                                <FilterAltOff />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )
                      }

                      if (field === UserGroupViewDTOFields.CountryName) {
                        return (
                          <CountryFilter
                            name={UserGroupFilterDTOFields.CodsSistemaPrincipal}
                            control={controlFiltro}
                            onReset={() => {
                              resetFieldFiltro(
                                UserGroupFilterDTOFields.CodsSistemaPrincipal
                              )
                              onFilterChange()
                            }}
                            onSearch={onFilterChange}
                          />

                        )
                      }

                      if (field === UserGroupViewDTOFields.NombreSistema) {
                        return (
                          <WorkflowFilter
                            name={UserGroupFilterDTOFields.CodsMensajeTipo}
                            control={controlFiltro}
                            onSearch={onFilterChange}
                            onReset={() => {
                              resetFieldFiltro(
                                UserGroupFilterDTOFields.CodsMensajeTipo
                              )
                              onFilterChange()
                            }}

                          />
                        )
                      }

                      return <></>
                    },
                    columnMenuIcon: FilterAlt
                  }}
                />
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant='text' color='primary' onClick={onCancelHandler}>
          Cancelar
        </Button>
        {
          !formDisabled &&
          <Button variant='contained' color='primary' promise={onSubmitWrapper}>
            Guardar
          </Button>
        }

      </DialogActions>
    </>
  )
}

export default EditUser
